import type { HTMLAttributes } from 'react';
import { useEffect } from 'react';

import { ArrowRight, CheckmarkCircle } from '@remarkable/ark-icons';
import { useLocation } from '@tanstack/react-router';

import { useAccessTokenClaims, useAuth0Claims } from 'src/api/queries/user';
import { Link2 } from 'src/components/Link2';
import { useNotifications } from 'src/hooks/useNotifications';
import { cn } from 'src/utils/classNamesHelper';
import { URLS } from 'src/utils/urls/urls';
import { userHelpers } from 'src/utils/userHelpers';

import { useHasActiveEnterpriseEnrollment } from 'src/api/queries/enterpriseEnrollment';
import { useEnterpriseSubscriptionQuery } from 'src/api/queries/enterpriseSubscription';
import { TestId } from 'src/testids';
import { connectSubscriptionHelpers } from 'src/utils/connectSubscriptionHelpers';
import { Notification } from './Notification';

export const HeaderNotification = () => {
  return <HeaderNotificationContent />;
};

function NotificationContainer({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mx-auto mt-24 max-w-lxl px-16 ls:px-32', className)}
      {...props}
    />
  );
}

const HeaderNotificationContent = () => {
  const location = useLocation();
  const claims = useAuth0Claims();
  const notifications = useNotifications();
  const hasActiveEnterpriseEnrollment = useHasActiveEnterpriseEnrollment();
  const accessTokenClaims = useAccessTokenClaims();
  const connectSubscription = useEnterpriseSubscriptionQuery({
    enabled:
      userHelpers.isLoggedInToOrganization(claims) &&
      userHelpers.hasPermissionReadSubscription(accessTokenClaims.data),
  });

  useEffect(() => {
    if (!claims.data) return;

    if (
      !userHelpers.isLinkable(claims.data) &&
      !userHelpers.isDomainClaimedByConnection(claims.data)
    ) {
      notifications.remove('EMAIL_DOMAIN_CLAIMED');
      return;
    }

    notifications.set({ id: 'EMAIL_DOMAIN_CLAIMED' }, { force: true });
  }, [claims.data, location]);

  useEffect(() => {
    if (!hasActiveEnterpriseEnrollment.isSuccess) return;

    if (hasActiveEnterpriseEnrollment.data === true) {
      notifications.set(
        { id: 'ENTERPRISE_ENROLLMENT_ONGOING' },
        { force: true }
      );
      return;
    }

    notifications.remove('ENTERPRISE_ENROLLMENT_ONGOING');
  }, [hasActiveEnterpriseEnrollment.data, location]);

  useEffect(() => {
    if (!connectSubscription.data) return;

    if (connectSubscriptionHelpers.isOverdue(connectSubscription.data)) {
      notifications.set({ id: 'CONNECT_OVERDUE' });
      return;
    }

    notifications.remove('CONNECT_OVERDUE');
  }, [connectSubscription.data, location]);

  // Remove subscription notifications when user navigates away from subscription page
  useEffect(() => {
    if (!location.pathname.startsWith(URLS.SUBSCRIPTION)) {
      switch (notifications.active[0]?.id) {
        case 'JOINED_CONNECT_SEATS_AS_MEMBER':
          notifications.remove('JOINED_CONNECT_SEATS_AS_MEMBER');
          break;
        case 'JOINED_CONNECT_SEATS_AS_MEMBER_WITH_REFUND':
          notifications.remove('JOINED_CONNECT_SEATS_AS_MEMBER_WITH_REFUND');
          break;
        case 'JOINED_CONNECT_SEATS_AS_OWNER':
          notifications.remove('JOINED_CONNECT_SEATS_AS_OWNER');
          break;
        case 'JOINED_CONNECT_SEATS_AS_OWNER_WITH_REFUND':
          notifications.remove('JOINED_CONNECT_SEATS_AS_OWNER_WITH_REFUND');
          break;
      }
    }
  }, [location.pathname]);

  switch (notifications.active[0]?.id) {
    case 'CONNECT_OVERDUE': {
      if (
        location.pathname.includes(URLS.ENTERPRISE_BILLING) ||
        !userHelpers.hasPermissionReadSubscription(accessTokenClaims.data)
      ) {
        break;
      }
      return (
        <Link2
          to={URLS.ENTERPRISE_BILLING}
          className="w-full no-underline"
          componentLocation="header notifications enterprise-connect-overdue"
        >
          <Notification
            className="justify-center border-none bg-feedback-red-500 py-8 text-white shadow-sheet transition-all hover:bg-[#c00000]"
            data-cy={TestId.BannerConnectOverdue}
          >
            <div className="m-auto flex items-center justify-center gap-16">
              <p className="responsive-body-md">
                Your Connect for Business invoice is past due. Please settle
                your balance to keep your subscription active.
              </p>

              <ArrowRight size={32} className="shrink-0 transition-all" />
            </div>
          </Notification>
        </Link2>
      );
    }

    case 'ENTERPRISE_ENROLLMENT_ONGOING': {
      if (
        location.pathname.includes(URLS.ENTERPRISE) ||
        location.pathname.includes(URLS.ACCOUNT_MIGRATE)
      ) {
        break;
      }
      return (
        <Link2
          to={URLS.ENTERPRISE_ENROLL}
          className="w-full no-underline"
          componentLocation="header notifications enterprise-enrollment-ongoing"
        >
          <Notification
            className="justify-center border-none bg-pen-blue py-8 text-white shadow-sheet transition-all hover:bg-pen-blue-dark"
            data-cy={TestId.BannerEnterpriseEnrollmentContinue}
          >
            <div className="m-auto flex items-center justify-center gap-16">
              <p className="responsive-body-md">
                Continue your enterprise enrollment process
              </p>

              <ArrowRight size={32} className="shrink-0 transition-all" />
            </div>
          </Notification>
        </Link2>
      );
    }

    case 'EMAIL_DOMAIN_CLAIMED': {
      if (
        location.pathname.includes(URLS.ENTERPRISE) ||
        location.pathname.includes(URLS.ACCOUNT_MIGRATE)
      ) {
        break;
      }

      return (
        <Link2
          to={URLS.ACCOUNT_MIGRATE}
          className="w-full no-underline"
          componentLocation="header notifications email-domain-claimed"
        >
          <Notification
            className="justify-center border-none bg-pen-blue py-8 text-white shadow-sheet transition-all hover:bg-pen-blue-dark"
            data-cy="email-domain-claimed"
          >
            <div className="m-auto flex items-center justify-center gap-16">
              <p className="responsive-body-md">
                Start using your enterprise account
              </p>

              <ArrowRight size={32} className="shrink-0 transition-all" />
            </div>
          </Notification>
        </Link2>
      );
    }
    case 'JOINED_CONNECT_SEATS_AS_OWNER':
      return (
        <NotificationContainer>
          <Notification
            data-cy="joined-connect-seats-as-owner-notification"
            onClick={() =>
              notifications.remove('JOINED_CONNECT_SEATS_AS_OWNER')
            }
          >
            <div className="flex">
              <CheckmarkCircle
                className="mr-16 inline-block shrink-0 text-feedback-green-500"
                size={32}
              />{' '}
              <p>You can now add users to your subscription.</p>
            </div>
          </Notification>
        </NotificationContainer>
      );

    case 'JOINED_CONNECT_SEATS_AS_OWNER_WITH_REFUND':
      return (
        <NotificationContainer>
          <Notification
            data-cy="joined-connect-seats-as-owner-with-refund-notification"
            onClick={() =>
              notifications.remove('JOINED_CONNECT_SEATS_AS_OWNER_WITH_REFUND')
            }
          >
            <div className="flex">
              <CheckmarkCircle
                className="mr-16 inline-block shrink-0 text-feedback-green-500"
                size={32}
              />{' '}
              <p>
                You can now add users to your subscription. The refund for your
                previous subscription is being processed. Go to{' '}
                <Link2
                  inline
                  to={URLS.SUBSCRIPTION_PAYMENT}
                  componentLocation="header notifications joined-connect-seats-as-owner-with-refund payment-history"
                >
                  Payment history
                </Link2>{' '}
                to find your receipt.
              </p>
            </div>
          </Notification>
        </NotificationContainer>
      );

    case 'JOINED_CONNECT_SEATS_AS_MEMBER':
      return (
        <NotificationContainer>
          <Notification
            data-cy="joined-connect-seats-as-member-notification"
            onClick={() =>
              notifications.remove('JOINED_CONNECT_SEATS_AS_MEMBER')
            }
          >
            <div className="flex">
              <CheckmarkCircle
                className="mr-16 inline-block shrink-0 text-feedback-green-500"
                size={32}
              />{' '}
              <p>You&apos;ve successfully joined Connect</p>
            </div>
          </Notification>
        </NotificationContainer>
      );

    case 'JOINED_CONNECT_SEATS_AS_MEMBER_WITH_REFUND':
      return (
        <NotificationContainer>
          <Notification
            data-cy="joined-connect-seats-as-member-with-refund-notification"
            onClick={() =>
              notifications.remove('JOINED_CONNECT_SEATS_AS_MEMBER_WITH_REFUND')
            }
          >
            <div className="flex">
              <CheckmarkCircle
                className="mr-16 inline-block shrink-0 text-feedback-green-500"
                size={32}
              />{' '}
              <p>
                You&apos;ve successfully joined Connect. The refund for your
                previous subscription is being processed. Go to{' '}
                <Link2
                  inline
                  to={URLS.SUBSCRIPTION_PAYMENT}
                  componentLocation="header notifications joined-connect-seats-as-member-with-refund payment-history"
                >
                  Payment history
                </Link2>{' '}
                to find your receipt.
              </p>
            </div>
          </Notification>
        </NotificationContainer>
      );

    default:
      return null;
  }
};
